import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// Analítica de uso del Chat. Arsenal ve global o por empresa; empresa ve la suya.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Chip, Stack, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Tooltip, CircularProgress, TextField, MenuItem, Button, Alert, Avatar, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Forum as ForumIcon, Download as DownloadIcon,
  Groups as GroupsIcon, Campaign as MentionIcon, AttachFile as AttachIcon,
  Call as CallIcon, Business as BusinessIcon, AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';

const getAdoption = (t) => ({
  alto: { label: t('hardcoded.pages_chatanalyticspage.alto'), color: '#2e7d32' },
  medio: { label: t('hardcoded.pages_chatanalyticspage.medio'), color: '#ed6c02' },
  bajo: { label: t('hardcoded.pages_chatanalyticspage.bajo'), color: '#d32f2f' },
  sin_uso: { label: t('hardcoded.pages_chatanalyticspage.sin_uso'), color: '#9e9e9e' },
});
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const Kpi = ({ icon, label, value, sub, color = '#1a1a1a' }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ py: 1.75 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Avatar sx={{ bgcolor: 'rgba(211,47,47,0.08)', color: 'primary.main', width: 28, height: 28 }}>{icon}</Avatar>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const AdoptionChip = ({ value }) => {
  const { t } = useTranslation();
  const adoption = useMemo(() => getAdoption(t), [t]);
  const a = adoption[value] || adoption.sin_uso;
  return <Chip size="small" label={a.label} sx={{ bgcolor: `${a.color}22`, color: a.color, fontWeight: 700, height: 20 }} />;
};

export default function ChatAnalyticsPage() {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [scope, setScope] = useState('company');
  const [tab, setTab] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.chatAnalytics.overview({ scope, days });
      setData(r.data);
    } catch (e) {
      showError(e?.response?.data?.detail || t('hardcoded.pages_chatanalyticspage.error_cargando_la_analitica_del'));
    } finally {
      setLoading(false);
    }
  }, [scope, days, showError, t]);

  useEffect(() => { load(); }, [load]);

  const isArsenal = data?.is_arsenal;
  const k = data?.kpis || {};
  const byCompany = data?.by_company || [];
  const byUser = data?.by_user || [];
  const byChannel = data?.by_channel || [];
  const insights = data?.insights || [];
  const globalScope = data?.scope === 'global';

  const doExport = async (table) => {
    try {
      const { url, token } = api.chatAnalytics.exportUrl({ scope, days, table });
      const resp = await fetch(url, { headers: { Authorization: t('hardcoded.components_common_imageupload.bearer_token', { token: token }) } });
      if (!resp.ok) throw new Error(t('hardcoded.components_audit_auditlog.export'));
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `uso_chat_${table}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { showError(t('hardcoded.pages_chatanalyticspage.no_se_pudo_exportar_el')); }
  };

  const tabs = [];
  if (globalScope) tabs.push(t('hardcoded.pages_chatanalyticspage.company'));
  tabs.push('user', t('hardcoded.pages_chatanalyticspage.channel'));
  const activeTable = tabs[tab] || 'user';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ForumIcon /> {t('hardcoded.components_common_sidebar.uso_del_chat')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {globalScope ? t('hardcoded.pages_chatanalyticspage.adopcion_del_chat_en_todas') : t('hardcoded.pages_chatanalyticspage.adopcion_del_chat_en_tu')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField select size="small" label={t('hardcoded.pages_billinginvoicespage.periodo')} value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 130 }}>
            <MenuItem value={7}>{t('hardcoded.pages_chatanalyticspage.ultimos_7_dias')}</MenuItem>
            <MenuItem value={30}>{t('hardcoded.pages_chatanalyticspage.ultimos_30_dias')}</MenuItem>
            <MenuItem value={90}>{t('hardcoded.pages_chatanalyticspage.ultimos_90_dias')}</MenuItem>
          </TextField>
          {isArsenal && (
            <TextField select size="small" label={t('hardcoded.components_ai_aimemoryeditor.alcance')} value={scope} onChange={(e) => { setScope(e.target.value); setTab(0); }} sx={{ minWidth: 150 }}>
              <MenuItem value="company">{t('hardcoded.pages_chatanalyticspage.mi_empresa')}</MenuItem>
              <MenuItem value="global">{t('hardcoded.components_common_sidebar.todas_las_empresas')}</MenuItem>
            </TextField>
          )}
          <Tooltip title={t('audit.actions.UPDATE')}><span><IconButton color="primary" onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        </Stack>
      </Stack>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          {/* KPIs */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {globalScope && <Grid item xs={6} sm={4} md={2}><Kpi icon={<BusinessIcon fontSize="small" />} label={t('hardcoded.pages_chatanalyticspage.empresas')} value={k.companies_using_chat ?? 0} color="#d32f2f" /></Grid>}
            <Grid item xs={6} sm={4} md={2}><Kpi icon={<GroupsIcon fontSize="small" />} label={t('audit.stats.activeUsers')} value={k.active_users ?? 0} sub={t('hardcoded.pages_chatanalyticspage.kclient_users_a_clientes', { kclient_users_a: k.client_users_active ?? 0 })} /></Grid>
            <Grid item xs={6} sm={4} md={2}><Kpi icon={<ForumIcon fontSize="small" />} label={t('hardcoded.pages_chatanalyticspage.mensajes')} value={k.messages ?? 0} /></Grid>
            <Grid item xs={6} sm={4} md={2}><Kpi icon={<MentionIcon fontSize="small" />} label={t('hardcoded.pages_chatanalyticspage.menciones')} value={k.mentions ?? 0} /></Grid>
            <Grid item xs={6} sm={4} md={2}><Kpi icon={<AttachIcon fontSize="small" />} label={t('hardcoded.pages_chatanalyticspage.adjuntos')} value={k.attachments_total ?? 0} sub={`${k.images ?? 0} img · ${k.audios ?? 0} audio · ${k.videos ?? 0} video`} /></Grid>
            <Grid item xs={6} sm={4} md={2}><Kpi icon={<CallIcon fontSize="small" />} label={t('hardcoded.pages_chatanalyticspage.llamadas')} value={k.calls_total ?? 0} sub={t('hardcoded.pages_chatanalyticspage.kcalls_answered_contestadas_kcalls_missed0_perdidas', { kcalls_answered: k.calls_answered ?? 0, kcalls_missed0: k.calls_missed ?? 0 })} /></Grid>
          </Grid>

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
            <Chip size="small" variant="outlined" label={t('hardcoded.pages_chatanalyticspage.canales_kchannels0', { kchannels0: k.channels ?? 0 })} />
            <Chip size="small" variant="outlined" label={t('hardcoded.pages_chatanalyticspage.audio_kcalls_audio0', { kcalls_audio0: k.calls_audio ?? 0 })} />
            <Chip size="small" variant="outlined" label={t('hardcoded.pages_chatanalyticspage.video_kcalls_video0', { kcalls_video0: k.calls_video ?? 0 })} />
            <Chip size="small" variant="outlined" label={t('hardcoded.pages_chatanalyticspage.ultima_actividad_fmtdateklast_ac', { fmtDateklast_ac: fmtDate(k.last_activity) })} />
          </Stack>

          {/* Insights IA/reglas */}
          {insights.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: 'rgba(211,47,47,0.25)' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AIIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>{t('hardcoded.pages_chatanalyticspage.recomendaciones_de_adopcion')}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                {insights.map((insight, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={insight.severity === 'warning' ? t('hardcoded.pages_chatanalyticspage.atencion') : t('common.info')}
                      color={insight.severity === 'warning' ? 'warning' : 'info'} sx={{ height: 18, fontSize: 10 }} />
                    <Typography variant="body2">{insight.text}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Tablas */}
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                {globalScope && <Tab label={t('hardcoded.pages_chatanalyticspage.por_empresa')} />}
                <Tab label={t('hardcoded.pages_chatanalyticspage.por_usuario')} />
                <Tab label={t('hardcoded.pages_chatanalyticspage.por_canal')} />
              </Tabs>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => doExport(activeTable)}>{i18n.t('audit.export.csv')}</Button>
            </Stack>
            <Divider />
            <TableContainer sx={{ maxHeight: 560 }}>
              {activeTable === 'company' && (
                <Table size="small" stickyHeader>
                  <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                    <TableCell>{i18n.t('navbar.company')}</TableCell><TableCell align="right">{i18n.t('users.title')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_adoptioncampaignspage.canales')}</TableCell>
                    <TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.mensajes')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.adjuntos')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.llamadas')}</TableCell>
                    <TableCell>{t('hardcoded.pages_chatanalyticspage.ultima_actividad')}</TableCell><TableCell>{t('hardcoded.pages_chatanalyticspage.adopcion')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {byCompany.map((r) => (
                      <TableRow key={r.company_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.company_name}</TableCell>
                        <TableCell align="right">{r.active_users}</TableCell><TableCell align="right">{r.channels}</TableCell>
                        <TableCell align="right">{r.messages}</TableCell><TableCell align="right">{r.attachments}</TableCell>
                        <TableCell align="right">{r.calls}</TableCell><TableCell>{fmtDate(r.last_activity)}</TableCell>
                        <TableCell><AdoptionChip value={r.adoption} /></TableCell>
                      </TableRow>
                    ))}
                    {byCompany.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('hardcoded.pages_chatanalyticspage.sin_datos')}</Typography></TableCell></TableRow>}
                  </TableBody>
                </Table>
              )}
              {activeTable === 'user' && (
                <Table size="small" stickyHeader>
                  <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                    <TableCell>{t('audit.table.user')}</TableCell><TableCell>{i18n.t('users.table.role')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.mensajes')}</TableCell>
                    <TableCell align="right">{t('hardcoded.pages_chatanalyticspage.menciones')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_adoptioncampaignspage.canales')}</TableCell>
                    <TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.adjuntos')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.llamadas')}</TableCell><TableCell>{t('hardcoded.pages_chatanalyticspage.ultima_actividad')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {byUser.map((r) => (
                      <TableRow key={r.user_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.name}{r.is_arsenal && <Chip size="small" label={t('hardcoded.components_common_sidebar.arsenal')} sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                        </TableCell>
                        <TableCell>{r.role}</TableCell><TableCell align="right">{r.messages_sent}</TableCell>
                        <TableCell align="right">{r.mentions_received}</TableCell><TableCell align="right">{r.channels_active}</TableCell>
                        <TableCell align="right">{r.attachments_sent}</TableCell><TableCell align="right">{r.calls_made}</TableCell>
                        <TableCell>{fmtDate(r.last_activity)}</TableCell>
                      </TableRow>
                    ))}
                    {byUser.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('hardcoded.pages_chatanalyticspage.sin_actividad_en_el_periodo')}</Typography></TableCell></TableRow>}
                  </TableBody>
                </Table>
              )}
              {activeTable === 'channel' && (
                <Table size="small" stickyHeader>
                  <TableHead><TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#fafafa' } }}>
                    <TableCell>{i18n.t('hardcoded.components_ai_assistantpanel.canal')}</TableCell><TableCell>{i18n.t('vehicles.form.type')}</TableCell>{globalScope && <TableCell>{i18n.t('navbar.company')}</TableCell>}
                    <TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.miembros')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.mensajes')}</TableCell>
                    <TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.adjuntos')}</TableCell><TableCell align="right">{i18n.t('hardcoded.pages_chatanalyticspage.llamadas')}</TableCell>
                    <TableCell>{t('hardcoded.pages_chatanalyticspage.ultimo_mensaje')}</TableCell><TableCell>{t('hardcoded.pages_chatanalyticspage.adopcion')}</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {byChannel.map((r) => (
                      <TableRow key={r.channel_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell><TableCell>{r.type}</TableCell>
                        {globalScope && <TableCell>{r.company_name}</TableCell>}
                        <TableCell align="right">{r.members}</TableCell><TableCell align="right">{r.messages}</TableCell>
                        <TableCell align="right">{r.attachments}</TableCell><TableCell align="right">{r.calls}</TableCell>
                        <TableCell>{fmtDate(r.last_message_at)}</TableCell><TableCell><AdoptionChip value={r.adoption} /></TableCell>
                      </TableRow>
                    ))}
                    {byChannel.length === 0 && <TableRow><TableCell colSpan={globalScope ? 9 : 8}><Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{t('hardcoded.pages_chatanalyticspage.sin_canales')}</Typography></TableCell></TableRow>}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
